import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const DAY_MS = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + DAY_MS;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + DAY_MS;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(DAY_MS);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const next = Date.now() + DAY_MS;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(DAY_MS);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = DAY_MS - remaining;
  const progress = Math.min(90, 65 + elapsed / DAY_MS * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-white border border-rose-700/30 rounded-2xl p-6 md:p-8" data-id="element-56" data-mp="1l13d1n2d7cxe">
      <div className="text-center" data-id="element-57" data-mp="2sbfyp8djcosj">
        <div className="flex items-center justify-center gap-2 text-rose-900 font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-58" data-mp="2sadbbaff94hd">
          <AlarmClock size={20} aria-hidden="true" data-id="element-59" data-mp="2jb9p3xkyk8yn" />
          <span data-id="element-60" data-mp="2jdvy81nhywiz">Launch pricing ends in:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" role="timer" aria-live="off" data-id="element-61" data-mp="2ec6mskwd4gud">
          <TimeBox value={pad(totalHours)} label="hrs" data-id="element-62" data-mp="24n56etlwnxfv" />
          <span className="text-3xl md:text-5xl font-bold text-rose-700" aria-hidden="true" data-id="element-63" data-mp="24llq53091me3">
            :
          </span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-64" data-mp="1vn0bhixweril" />
          <span className="text-3xl md:text-5xl font-bold text-rose-700" aria-hidden="true" data-id="element-65" data-mp="1vobdpebp3r7l">
            :
          </span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-66" data-mp="1lo10htpnujsf" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-stone-600 mb-8" data-id="element-67" data-mp="168ldjx2kc97">
          After this the price goes up and it will not come back down.
        </p>

        <div className="space-y-2" data-id="element-68" data-mp="67frlejkd3dr">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-69" data-mp="b3wkem1pk5x6">
            <span className="text-stone-500" data-id="element-70" data-mp="f6b2v51gt3nx">Launch price spots remaining</span>
            <span className="text-rose-700" data-id="element-71" data-mp="3elepwmu0d0uf">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-stone-100 rounded-full overflow-hidden border border-stone-200" data-id="element-72" data-mp="3ejbs3gmm63f5">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-full bg-rose-600 rounded-full" data-id="element-73" data-mp="3nlurfqr6xxwn" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-stone-500 font-medium" data-id="element-74" data-mp="2gs5a7lwscrqq">
            {Math.round(progress)}% of spots taken
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-stone-100 border border-rose-700/30 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-20 md:min-w-28" data-id="element-75" data-mp="2lpozrhyxoqxz">
      <div className="flex items-baseline justify-center gap-1" data-id="element-76" data-mp="2zgk20bexg9sl">
        <span className="text-4xl md:text-5xl font-bold text-stone-800 tabular-nums" data-id="element-77" data-mp="2ujtl5lvata64">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-rose-800" data-id="element-78" data-mp="39ezt4edyfj23">
          {label}
        </span>
      </div>
    </div>;
}